// The before/after view (docs/ai-edit-rights-brief.md, Spencer 2026-10-21): one row an AI employee touched (a job item,
// a scope row, the job's own setup, a catalog item with its recipes, a recipe, an operation, a material), everything
// that changed on it and under it since the version Spencer last verified (old beside new, who, when; the edits with
// their reason and source), and two buttons: Verify, one action for the whole row; Send back with a note, which puts
// the row back to that verified version and tells the editor. Ten edits to one item are one review, not ten.
// Nothing here prices anything; the trail is the database's (ug.changes_since_verified), netted by the engine.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { buildJob } from '@zerogap/db';
import { changesSinceVerified, resolveJob, type VerificationChange, type VerificationChangeRow } from '@zerogap/engine';
import { useCrumb } from '../frame/Frame';
import { refresh, rpc, toast, useRows, type Row } from '../lib/api';
import { longDate, money, shortDate } from '../lib/format';
import { useJobData, useResolved } from '../lib/job';
import { useLibrary } from '../lib/library';
import { useSession } from '../lib/session';
import { sayError, sendBackRow, suggestionTouches, verifyRow } from '../lib/suggest';
import { Btn, Empty, Head, Info, Loading, Modal, Money, Panel, Tag } from '../ui/kit';
import { fieldName, rowName, shown, useLook } from './CheckSheet';

type Table = 'job_item' | 'scope_row' | 'job' | 'catalog_item' | 'recipe' | 'operation' | 'material' | 'job_type';
const TABLES: Table[] = ['job_item', 'scope_row', 'job', 'catalog_item', 'recipe', 'operation', 'material', 'job_type'];
const JOB_TABLES = new Set(['job_item', 'scope_row', 'job']);

/** The jsonb columns a job row carries: a change inside one is said key by key, not as two blobs. */
const KEYED = new Set(['inputs', 'overrides', 'custom_route', 'custom_bom', 'global_overrides', 'pricing_overrides', 'job_item_ids']);

const asObject = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);
const text = (v: unknown): string => (v === null || v === undefined || v === '' ? 'blank' : typeof v === 'object' ? JSON.stringify(v) : String(v));

/** A change inside a keyed column, one line per key that moved. */
function keyed(was: unknown, now: unknown): { key: string; was: unknown; now: unknown }[] {
  const a = asObject(was) ?? {};
  const b = asObject(now) ?? {};
  if (!asObject(was) && !asObject(now)) return [{ key: '', was, now }];
  const keys = [...new Set([...Object.keys(a), ...Object.keys(b)])].sort();
  return keys.filter((k) => JSON.stringify(a[k]) !== JSON.stringify(b[k])).map((k) => ({ key: k, was: a[k], now: b[k] }));
}

export function Verify() {
  const { table = '', id = '' } = useParams();
  const t = TABLES.includes(table as Table) ? (table as Table) : null;
  const nav = useNavigate();
  const s = useSession();
  const look = useLook();
  const row = useRows(t ?? 'job_item', { eq: { id }, enabled: !!t && !!id });
  const r = row.rows[0] ?? null;
  const users = useRows('user');
  const who = (uid: unknown) => users.rows.find((u) => u.id === uid)?.name ?? (uid ? 'someone' : '');
  // where it belongs: the job for a job row (a scope row through its opportunity), the catalog item for a recipe
  const jobId: string | null = t === 'job' ? id : t === 'job_item' ? (r?.job_id as string | null) ?? null : null;
  const oppJobs = useRows('job', { eq: { opportunity_id: r?.opportunity_id }, order: ['created_at'], enabled: t === 'scope_row' && !!r?.opportunity_id });
  const scopeJob = t === 'scope_row' ? (oppJobs.rows[0]?.id as string | undefined) ?? null : null;
  const catalogId: string | null = t === 'catalog_item' ? id : t === 'recipe' ? (r?.catalog_item_id as string | null) ?? null : null;
  const catalog = useRows('catalog_item', { eq: { id: catalogId }, enabled: !!catalogId });
  const recipes = useRows('recipe', { eq: { catalog_item_id: catalogId }, order: ['name'], enabled: !!catalogId });
  const items = useRows('job_item', { eq: { job_id: jobId ?? scopeJob }, enabled: !!(jobId ?? scopeJob) });
  const jobRow = useRows('job', { eq: { id: jobId ?? scopeJob }, enabled: !!(jobId ?? scopeJob) });
  const opp = useRows('opportunity', { eq: { id: jobRow.rows[0]?.opportunity_id }, enabled: !!jobRow.rows[0]?.opportunity_id });
  // the trail since the last verified version, netted by the engine
  const trail = useQuery({ queryKey: ['changes_since_verified', t, id], queryFn: () => rpc<VerificationChangeRow[]>('changes_since_verified', { p_table: t, p_id: id }), enabled: !!t && !!id });
  // changes made in one transaction share a timestamp: the trail's own id puts them in order before the netting
  const history = useMemo(() => changesSinceVerified([...(trail.data ?? [])].sort((a, b) => a.changed_at.localeCompare(b.changed_at) || Number((a as { audit_id?: number }).audit_id ?? 0) - Number((b as { audit_id?: number }).audit_id ?? 0))).get(`${t}:${id}`), [trail.data, t, id]);
  // the trail's own starting point (a job row never checked reads from the AI's first touch); what the screen calls "last verified" is the row's own date
  const since: string | null = trail.data?.[0]?.verified_at && !String(trail.data[0].verified_at).startsWith('-infinity') ? String(trail.data[0].verified_at) : null;
  const verifiedAt: string | null = t && JOB_TABLES.has(t) ? ((r?.checked_at as string | null) ?? null) : since;
  // the edits themselves: the applied (or approved) suggestions on this row since then, with their reason and source
  const suggestions = useRows('suggestion', { eq: { job_id: t && JOB_TABLES.has(t) ? (jobId ?? scopeJob) : null }, order: ['decided_at'], enabled: !t || !JOB_TABLES.has(t) || !!(jobId ?? scopeJob) });
  const edits = useMemo(
    // the edits that reached the row: applied directly, approved, or sent back after they were applied (never a suggestion that only waited)
    () => suggestions.rows.filter((x) => (x.status === 'applied' || x.status === 'approved' || (x.status === 'sent_back' && x.applied_value !== null && x.applied_value !== undefined)) && !!x.decided_at && (!since || String(x.decided_at) > since) && suggestionTouches(x, t ?? '', id, recipes.rows.map((rc) => rc.id as string))),
    [suggestions.rows, since, t, id, recipes.rows],
  );
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  // F13 (2026-10-24): a job item's price before and after the edits. After: the item as the job prices it now. Before:
  // the same job with the item's rows (and the sub lines on it) put back to the verified version from the trail, priced
  // the same way. Both are the engine's numbers; nothing is stored.
  const jd = useJobData(t === 'job_item' && jobId ? jobId : undefined);
  const rv = useResolved(jd);
  const { rows: libRows } = useLibrary(jd.job?.standards_version_id ?? null);
  const priceLine = useMemo(() => {
    if (t !== 'job_item' || !rv.lib || !rv.res || jd.loading || !history) return null;
    const now = rv.res.items.find((i) => i.id === id)?.rollup_price_cents ?? null;
    if (now === null) return null;
    try {
      const items = [...jd.items.map((r) => ({ ...r }))];
      const subs = [...jd.subLines.map((r) => ({ ...r }))];
      const back = (list: Row[], c: VerificationChange) => {
        const at = list.findIndex((r) => r.id === c.row_id);
        if (c.kind === 'added') {
          if (at >= 0) list.splice(at, 1);
        } else if (c.kind === 'removed') {
          if (at < 0) list.push({ ...(c.row as Row) });
        } else if (at >= 0 && c.field) list[at] = { ...list[at]!, [c.field]: c.was };
      };
      for (const c of history.changes) {
        if (c.table === 'job_item') back(items, c);
        if (c.table === 'sub_line') back(subs, c);
      }
      const jobIn = buildJob(
        { job: jd.job!, job_standard: jd.jobStandards, job_item: items, sub_line: subs, decision_line: jd.decisionLines, job_cost_line: jd.costLines, room: jd.rooms, room_standard: jd.roomStandards, job_type_route_step: jd.typeSteps, job_type_bom_line: jd.typeBomLines, job_route_line: jd.routeLines, job_bom_line: jd.bomLines },
        libRows.standard_def,
      );
      const before = resolveJob(rv.lib, jobIn).items.find((i) => i.id === id)?.rollup_price_cents ?? null;
      return { before, now };
    } catch {
      return { before: null, now };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, id, rv.lib, rv.res, jd.loading, jd.items, jd.subLines, history]);

  const name = (() => {
    if (!r) return '…';
    switch (t) {
      case 'job_item':
        return `${r.number} · ${r.name || ''}`;
      case 'scope_row':
        return 'Scope row';
      case 'job':
        return `${opp.rows[0]?.name ?? ''} · ${r.scope_name ?? ''} · job setup`;
      case 'recipe':
        return `${catalog.rows[0]?.name ?? ''} · ${r.name} recipe`;
      case 'material':
        return String(r.description ?? '');
      default:
        return String(r.name ?? '');
    }
  })();
  const back = t === 'job' ? `/jobs/${id}?tab=details` : t === 'job_item' ? `/jobs/${jobId}?tab=details&item=${id}` : t === 'scope_row' ? (scopeJob ? `/jobs/${scopeJob}?tab=scope` : '/jobs') : catalogId ? `/library/catalog/${catalogId}` : `/library/${t}?row=${id}`;
  const backLabel = t === 'job' || t === 'job_item' ? 'the job' : t === 'scope_row' ? 'the Scope tab' : catalogId ? 'the catalog editor' : 'the Library';
  const unverified = r ? (t && JOB_TABLES.has(t) ? !!r.unverified_since : t === 'catalog_item' ? !r.verified || recipes.rows.some((x) => !x.verified) : !r.verified) : false;
  const canVerify = s.can('verify') && !s.user?.is_ai && s.canWrite;

  useCrumb(t && JOB_TABLES.has(t) ? [{ label: 'Jobs', to: '/jobs' }, { label: opp.rows[0]?.name ?? '…', to: jobId ?? scopeJob ? `/jobs/${jobId ?? scopeJob}` : undefined }, { label: 'Verify' }] : [{ label: 'Library', to: '/library' }, { label: name }, { label: 'Verify' }]);

  if (!t) return <Empty>There is nothing to verify at this address.</Empty>;
  if (row.loading || trail.isLoading) return <Loading what="Reading what changed" />;
  if (!r) return <Empty>That row is no longer here.</Empty>;

  const verify = async () => {
    setBusy(true);
    try {
      await verifyRow(t, id);
      toast(`Verified: ${name}.`);
      nav(back);
    } catch (e) {
      sayError(e);
    } finally {
      setBusy(false);
    }
  };
  const sendBack = async () => {
    setBusy(true);
    try {
      const out = (await sendBackRow(t, id, note)) as { removed?: boolean; changes_put_back?: number; suggestions_sent_back?: number };
      toast(out.removed ? `Removed: ${name} had no verified version to go back to. ${out.suggestions_sent_back ?? 0} edit(s) sent back with your note.` : `${name} is back to the version you verified; ${out.suggestions_sent_back ?? 0} edit(s) sent back with your note.`);
      setSending(false);
      nav(back);
    } catch (e) {
      sayError(e);
    } finally {
      setBusy(false);
    }
  };

  const changes = history?.changes ?? [];
  // the whole value, never cut with an ellipsis: the words he came to judge (Iris, 2026-10-22)
  const full = (field: string, key: string, v: unknown) => (key ? text(v) : typeof v === 'string' && v.length > 88 ? v : shown(field, v, look));
  // F14 (2026-10-24): one list. Each change carries the edit behind it (its reason, source and who), matched by the row and
  // the field it names; an edit that matches no change in the trail (a send-back, a value changed back) is listed on its own.
  const editValue = (x: Row) => text(x.status === 'applied' || x.status === 'approved' ? x.applied_value ?? x.new_value : x.new_value);
  const editsFor = (c: VerificationChange) =>
    edits.filter((x) => {
      const p = (x.payload ?? {}) as Record<string, unknown>;
      const onRow = x.target_id === c.row_id || p.job_item_id === c.row_id || (c.table === t && c.row_id === id);
      if (!onRow) return false;
      const key = (x.target_key ?? p.key ?? p.field) as string | undefined;
      return !key || !c.field || key === c.field || String(key) === `${c.field}.${''}` || (c.field && String(key).startsWith(`${c.field}`));
    });
  const matched = new Set<string>();
  const editNote = (x: Row, withValue: boolean) => (
    <small className="vedit" key={x.id}>
      {x.status === 'applied' ? <Tag kind="un">changed directly</Tag> : x.status === 'approved' ? <Tag kind="on">approved</Tag> : <Tag kind="off">sent back</Tag>} {who(x.suggested_by)} · {shortDate(x.decided_at)}
      {withValue ? ` · ${String(x.kind).replace(/_/g, ' ')}${x.target_key ? ` · ${String(x.target_key).replace(/_/g, ' ')}` : ''}: ${text(x.old_value)} → ${editValue(x)}` : ''}
      {x.reason ? ` · ${x.reason}` : ''}
      {x.source ? ` · ${String(x.source).slice(0, 80)}` : ''}
    </small>
  );
  const rowWords = (c: VerificationChange) => {
    if (c.table === t && c.row_id === id) return t === 'job' ? 'the job' : t === 'scope_row' ? 'the row' : 'the item';
    const known = rowName(c, look);
    if (known) return known;
    const rr = c.row;
    switch (c.table) {
      case 'job_item':
        return `${rr.number ?? ''} ${rr.name ?? ''}`.trim() || 'a child item';
      case 'sub_line':
        return `sub line ${rr.scope_text ?? ''}`;
      case 'job_standard':
        return `standard ${look('standard_def', rr.standard_def_id) || ''}`.trim();
      case 'job_route_line':
        return `job route ${look('operation', rr.operation_id)}`;
      case 'job_bom_line':
        return `job BOM ${look('material', rr.material_id)}`;
      case 'job_cost_line':
        return `job-wide cost ${rr.label ?? ''}`;
      case 'build':
        return `build ${rr.name ?? ''}`;
      case 'add_on':
        return `add-on ${rr.name ?? ''}`;
      case 'room':
        return `room ${rr.name ?? ''}`;
      case 'recipe':
        return `recipe ${rr.name ?? ''}`;
      case 'time_standard':
        return 'minutes';
      default:
        return c.table.replace(/_/g, ' ');
    }
  };

  return (
    <div className="stack">
      <Head
        title={name}
        sub={
          <>
            {t === 'job_item' ? 'Job item' : t === 'scope_row' ? String(r.request_text ?? '') : t === 'job' ? 'Job setup' : t === 'catalog_item' ? 'Catalog item, with every recipe under it' : t.replace('_', ' ')}
            {' · '}
            {unverified ? (
              <>
                <Tag kind="un">unverified</Tag>
                {r.unverified_since ? ` since ${longDate(r.unverified_since)} · ${who(r.unverified_by)}` : ''}
              </>
            ) : (
              <Tag kind="on">verified</Tag>
            )}
            {verifiedAt ? ` · last verified ${longDate(verifiedAt)}` : ' · never verified'}
          </>
        }
      >
        <span className="row mid" style={{ gap: 6, flexWrap: 'wrap' }}>
          {canVerify && unverified && (
            <Btn primary disabled={busy} onClick={() => void verify()} title={t === 'catalog_item' ? 'Signs the item and every recipe under it, in one tap.' : 'Signs this row as read and right.'}>
              {t === 'catalog_item' && recipes.rows.filter((x) => !x.verified).length > 0 ? `Verify item + ${recipes.rows.filter((x) => !x.verified).length} ${recipes.rows.filter((x) => !x.verified).length === 1 ? 'recipe' : 'recipes'}` : 'Verify'}
            </Btn>
          )}
          {canVerify && unverified && (
            <Btn disabled={busy} onClick={() => setSending(true)} title={verifiedAt ? 'Puts it back to the version you verified and tells the editor why.' : 'Puts it back to what it was before the AI employee touched it (a job item or scope row an AI made is removed), with your note to the editor.'}>
              Send back
            </Btn>
          )}
          <Link to={back} className="muted" style={{ alignSelf: 'center' }}>
            Back to {backLabel}
          </Link>
        </span>
      </Head>

      {t === 'catalog_item' && recipes.rows.length > 0 && (
        <Panel label="Recipes">
          {recipes.rows.map((x) => (
            <div key={x.id} className="ln">
              <span>
                <Link to={`/library/catalog/${id}?recipe=${x.id}`}>{x.name}</Link>
              </span>
              <span>{x.verified ? <Tag kind="on">verified</Tag> : <Tag kind="un">unverified</Tag>}</span>
            </div>
          ))}
          <small>One recipe on its own is signed in the editor.</small>
        </Panel>
      )}

      {priceLine && (
        <Panel label="Price" right={<Info left>The item's rolled-up price (with its doors, fronts and drawer boxes) as the job prices it now, and the same item with these changes put back to the version you verified, priced the same way. Both are the engine's numbers.</Info>}>
          <div className="vprice">
            <span>
              <small>before</small>
              {priceLine.before === null ? <span className="muted">—</span> : <Money cents={priceLine.before} />}
            </span>
            <span className="arrow">→</span>
            <span>
              <small>after the edits</small>
              <b>
                <Money cents={priceLine.now} />
              </b>
            </span>
            {priceLine.before !== null && priceLine.before !== priceLine.now && <Tag kind={priceLine.now > priceLine.before ? 'un' : 'off'}>{priceLine.now > priceLine.before ? '+' : '−'}{money(Math.abs(priceLine.now - priceLine.before))}</Tag>}
            {priceLine.before === priceLine.now && <Tag kind="off">no change to the price</Tag>}
          </div>
        </Panel>
      )}

      <Panel
        label={verifiedAt ? `Changed since you verified it on ${longDate(verifiedAt)} · ${changes.length}` : since ? `Changed since the AI employee touched it · ${changes.length}` : `Never verified · everything on it is new to you · ${changes.length}`}
        soft={changes.length > 0}
        right={<Info left>Every field that moved on this row and the rows under it, old beside new, with when and who, and under each the edit behind it: who made it, the reason it gave and where the figure came from. A field changed and changed back is not listed. Sent back: you put the row back and the editor read your note.</Info>}
      >
        {changes.length === 0 && edits.length === 0 ? (
          <Empty>{verifiedAt ? 'Nothing it says has changed since.' : 'Nothing is on the trail yet.'}</Empty>
        ) : (
          <div className="tblwrap">
            <table className="fixed suggest verify">
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '18%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Where</th>
                  <th>What</th>
                  <th>Was</th>
                  <th>Now</th>
                  <th>When · who</th>
                </tr>
              </thead>
              <tbody>
                {changes.flatMap((c, i) => {
                  const when = (
                    <td className="nowrap" data-l="when">
                      {shortDate(c.at)}
                      {c.by ? ` · ${who(c.by)}` : ''}
                    </td>
                  );
                  const behind = editsFor(c);
                  for (const x of behind) matched.add(x.id as string);
                  const why = behind.length > 0 && (
                    <tr key={`${i}-why`} className="why">
                      <td colSpan={5} data-l="edit">{behind.map((x) => editNote(x, false))}</td>
                    </tr>
                  );
                  // a row an AI retired (2026-10-23) reads as removed, behind Show retired in the editor; a restore as restored; Send back undoes either
                  if (c.kind === 'changed' && c.field === 'retired_at') {
                    return [
                      <tr key={i} className="changed">
                        <td data-l="where">{rowWords(c)}</td>
                        <td colSpan={3} data-l="what">
                          <b>{c.now ? 'removed · retired, behind Show retired' : 'restored'}</b>
                        </td>
                        {when}
                      </tr>,
                      why,
                    ];
                  }
                  if (c.kind !== 'changed') {
                    return [
                      <tr key={i} className="changed">
                        <td data-l="where">{rowWords(c)}</td>
                        <td colSpan={3} data-l="what">
                          <b>{c.kind === 'added' ? 'added' : 'removed'}</b>
                        </td>
                        {when}
                      </tr>,
                      why,
                    ];
                  }
                  const parts = KEYED.has(c.field!) ? keyed(c.was, c.now) : [{ key: '', was: c.was, now: c.now }];
                  return [
                    ...parts.map((p, j) => (
                      <tr key={`${i}-${j}`} className="changed">
                        <td data-l="where">{rowWords(c)}</td>
                        <td data-l="what">
                          {fieldName(c.table, c.field!)}
                          {p.key ? ` · ${p.key.replace(/_/g, ' ')}` : ''}
                        </td>
                        <td data-l="was">
                          <s>{full(c.field!, p.key, p.was)}</s>
                        </td>
                        <td data-l="now">
                          <b>{full(c.field!, p.key, p.now)}</b>
                        </td>
                        {when}
                      </tr>
                    )),
                    why,
                  ];
                })}
                {edits
                  .filter((x) => !matched.has(x.id as string))
                  .map((x) => (
                    <tr key={`e-${x.id}`} className="changed">
                      <td data-l="where">{t === 'job' ? 'the job' : t === 'scope_row' ? 'the row' : 'the item'}</td>
                      <td data-l="what">
                        {String(x.kind).replace(/_/g, ' ')}
                        {x.target_key ? ` · ${String(x.target_key).replace(/_/g, ' ')}` : ''}
                      </td>
                      <td data-l="was">
                        <s>{text(x.old_value)}</s>
                      </td>
                      <td data-l="now">
                        <b>{editValue(x)}</b>
                        <br />
                        {editNote(x, false)}
                      </td>
                      <td className="nowrap" data-l="when">
                        {shortDate(x.decided_at)} · {who(x.suggested_by)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {sending && (
        <Modal title={`Send back: ${name}`} onClose={() => setSending(false)}>
          <div className="stack">
            <p>
              {verifiedAt
                ? `Puts it back to the version you verified on ${longDate(verifiedAt)}: every change above is undone, and the editor reads your note.`
                : since
                  ? 'Puts it back to what it was before the AI employee touched it: every change above is undone, and the editor reads your note. A job item or scope row the AI itself made is removed instead.'
                  : 'There is no verified version to go back to. A job item or scope row an AI employee made is removed, and the editor reads your note.'}
            </p>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why it goes back, for the editor" rows={3} autoFocus />
            <div className="row" style={{ gap: 6 }}>
              <Btn primary disabled={busy || !note.trim()} onClick={() => void sendBack()}>
                Send back
              </Btn>
              <Btn onClick={() => setSending(false)}>Keep it</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** The tag a job row carries while an AI's edit on it waits for Spencer (the before/after view is one tap away). */
export function verifyAddressFor(table: Table, id: string): string {
  return `/verify/${table}/${id}`;
}

/** Refresh what a verification or a send-back may have changed. */
export function refreshAfterVerify(): void {
  refresh('job_item', 'scope_row', 'job', 'catalog_item', 'recipe', 'operation', 'material', 'suggestion', 'changes_since_verified', 'verification_changes', 'sub_line', 'job_standard', 'job_route_line', 'job_bom_line', 'job_cost_line', 'recipe_route_step', 'recipe_bom_line', 'catalog_part', 'catalog_input', 'catalog_derived', 'catalog_child', 'time_standard');
}
